import { SearchEntityKind, type SearchResult } from './model';

export const RECENT_SEARCHES_STORAGE_KEY = 'shigo.search.recent';
const MAX_RECENT_SEARCHES = 8;
const searchEntityKinds: ReadonlySet<string> = new Set(Object.values(SearchEntityKind));

export function readRecentSearches(storage: Storage | null = safeStorage()): SearchResult[] {
  try {
    const raw = storage?.getItem(RECENT_SEARCHES_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isRecentSearchEntry) : [];
  } catch {
    return [];
  }
}

export function pushRecentSearch(
  entry: SearchResult,
  storage: Storage | null = safeStorage(),
): SearchResult[] {
  const deduped = readRecentSearches(storage).filter(
    (existing) => !(existing.kind === entry.kind && existing.id === entry.id),
  );
  const next = [entry, ...deduped].slice(0, MAX_RECENT_SEARCHES);
  try {
    storage?.setItem(RECENT_SEARCHES_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Search navigation remains available when browser storage is unavailable.
  }
  return next;
}

export function clearRecentSearches(storage: Storage | null = safeStorage()) {
  try {
    storage?.removeItem(RECENT_SEARCHES_STORAGE_KEY);
  } catch {
    // Unavailable storage must not block logout.
  }
}

function isRecentSearchEntry(value: unknown): value is SearchResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['kind'] === 'string' &&
    searchEntityKinds.has(candidate['kind']) &&
    typeof candidate['id'] === 'string' &&
    typeof candidate['label'] === 'string' &&
    typeof candidate['to'] === 'string' &&
    candidate['to'].startsWith('/') &&
    typeof candidate['params'] === 'object' &&
    candidate['params'] !== null
  );
}

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
